import {
  ForwardedRef,
  forwardRef,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
} from 'react';
import { Scalars } from './apollo/graphqlSchema';

export enum ErrorType {
  NullPointerException = 'NullPointerException',
}

/**
 * pick selected enum keys from T
 */
export type PickEnum<T, K extends T> = {
  [P in keyof K]: P extends K ? P : never;
};

export type EmptyObject = Record<never, never>;

export function isNotNil<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined;
}

export function assertNotNil<T>(arg: T): asserts arg is NonNullable<T> {
  if (!isNotNil(arg)) {
    throw Error(ErrorType.NullPointerException);
  }
}

export type NotNullable<T> = T extends null | undefined ? never : T;

type Assert = (condition: unknown, message?: string) => asserts condition;
export const assertCondition: Assert = (condition, message) => {
  if (condition === false) {
    throw Error(message ?? 'Invalid assertion');
  }
};

/**
 * Adapted from: https://stackoverflow.com/a/58473012
 *
 * There is a TypeScript 3.4 feature called higher order function type
 * inference: https://github.com/microsoft/TypeScript/pull/30215
 *
 * It basically allows you to propagate
 * free type parameters (generics from the input function) on to the outer,
 * calling function (React.forwardRef here), so the resulting function
 * component is still generic.
 *
 * But this feature can only work with plain function types, as Anders Hejlsberg
 * explains:
 *
 * > We only make higher order function type inferences when the source and
 * > target types are both pure function types, i.e. types with a single call
 * > signature and no other members.
 *
 * Unfortunately, forwardRef definition adds properties like displayName,
 * defaultProps, propTypes which despite that are `never`, they make the
 * forwardRef impure.
 *
 * To avoid redefinition of React types, we define HofForwardRef that only
 * exposes the "pure" side of forwardRef. Then we wrap forwardRef as
 * `hofForwardRef` function: the return type of HofForwardRef is compatible
 * with original `forwardRef` because it is just a subset of when
 * forwardRef produces. Now `hofForwardRef` is now pure and TypeScript
 * allows HOF type inference
 *
 */
type HofForwardRef<P, T> = (
  render: (props: P, ref: ForwardedRef<T>) => ReactElement<P>,
) => (props: PropsWithoutRef<P> & RefAttributes<T>) => ReactElement<P> | null;

export const hofForwardRef = <T, P>(
  render: Parameters<HofForwardRef<T, P>>[0],
): ReturnType<HofForwardRef<T, P>> => forwardRef(render);

/**
 * Typescript-narrowing variant of Object.prototype.hasOwnProperty
 *
 * @param obj which we test
 * @param prop the prop of [obj]
 * @return true if [prop] is own property of [obj]
 */
export const hasProperty = <T>(obj: T, prop: PropertyKey): prop is keyof T =>
  Object.prototype.hasOwnProperty.call(obj, prop);

// react-select specific types start'
// ======
// a built-in react-select type but not exported
// that's why we have a copy of it
export interface FilterOptionOption<Option> {
  readonly label: string;
  readonly value: string;
  readonly data: Option;
}

// a common interface that usually has to be extended with
// feature-specific fields
export interface IReactSelectOption<V = string> {
  label: string;
  value: V;
  isDisabled?: boolean;
}

// a common interface for creatable menus
export interface ICreatableReactSelectOption<V = string>
  extends IReactSelectOption<V> {
  __isNew__?: boolean;
}
// ======
// react-select specific types end

/**
 * The missing Nullable type from the standard library. The opposite of NonNullable<T>
 */
export type Nullable<T> = { [P in keyof T]: T[P] | null };

/**
 * Helper to filter and narrow a list of union type to concrete instance
 * @param typeName
 */
export const byTypeName =
  <E extends { __typename: string }, TYPE_NAME extends E['__typename']>(
    typeName: TYPE_NAME,
  ) =>
  (element: E): element is E & { __typename: TYPE_NAME } =>
    element.__typename === typeName;

/**
 * Starting from typescript and typescript-operations plugin v4,
 * Inputs and outputs are now separate. These are aliases for convenience
 * as we mostly use output types
 *
 * https://the-guild.dev/blog/the-complete-graphql-scalar-guide
 */
export type GraphQLID = Scalars['ID']['output'];
export type GraphQLString = Scalars['String']['output'];
export type GraphQLBoolean = Scalars['Boolean']['output'];
export type GraphQLInt = Scalars['Int']['output'];
export type GraphQLFloat = Scalars['Float']['output'];
export type GraphQLBigDecimal = Scalars['BigDecimal']['output'];
export type GraphQLDate = Scalars['Date']['output'];
export type GraphQLLocalDate = Scalars['LocalDate']['output'];
export type GraphQLLong = Scalars['Long']['output'];
export type GraphQLUserAliasId = Scalars['UserAliasId']['output'];

/**
 * Add types of external libraries here
 * https://stackoverflow.com/questions/12709074/how-do-you-explicitly-set-a-new-property-on-window-in-typescript
 */
declare global {
  // ignore eslint for cognito configuration, otherwise Window is not considered as a used type
  // eslint-disable-next-line
  interface Window {
    // trigger userpilot events
    userpilot: {
      trigger: (event: string) => void;
    };
    userpilotRC: {
      hide: () => void;
    };
    // dataLayer for Intercom to pass category data
    dataLayer: { push: (data: any) => void };
    AmazonCognitoAdvancedSecurityData: {
      getData: (
        username: string,
        userPoolId: string,
        clientId: string,
      ) => string;
    };
  }
}

/**
 * Extract a member of GraphQL union data type
 */
export type GraphQLUnionMember<
  T extends { __typename: string },
  K extends T['__typename'],
> = T & {
  __typename: K;
};

/**
 * Helps to add proper types when working with Object.entries
 * https://www.charpeni.com/blog/properly-type-object-keys-and-object-entries#solution-1
 */
export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];
